/**
 * SEIS plan-type mapping — pure, no Firebase.
 *
 * SEIS exports a coded plan type such as "I100 Individualized Education
 * Program (IEP)". Reading it is strictly better than inferring IEP from a
 * student's mere presence in the export: it is evidence rather than assumption.
 *
 * A BLANK plan type usually means the student is in the referral/assessment
 * process and has no plan document yet — an initial IEP. Recognized value-level
 * conventions can be mapped automatically, but an initial result remains a
 * per-student decision: buildInitialConfirmationOverrides gives the parent flow
 * an enforceable gate instead of silently assigning initial status.
 */

import { PLAN_STATUS_ACTIVE, PLAN_STATUS_INITIAL, storedPlanValue } from './planStatus.js'
import { mapSeisField, extractSsidFromImportRow, normalizeSeisGrade } from './seisImport.js'
import {
  eligibilityBlocksInitial,
  exitedBlocksInitial,
  isStaleSeisPending,
  meetingTypeBlocksInitial
} from './seisPlanFlags.js'
import { normalizeSeisEligibilityStatus } from './planEvidence.js'

/** Sentinel for "this value means no plan should be assigned". */
export const PLAN_TYPE_NONE = '__NONE__'

/** Sentinel key for rows whose plan-type cell is empty. */
export const BLANK_PLAN_TYPE = '__BLANK__'

/**
 * What a mapped plan type resolves to. `status` rides alongside the plan so an
 * initial student stays visible everywhere `plan` is read — see planStatus.js.
 */
export const PLAN_TYPE_TARGETS = [
  { value: 'IEP', label: 'IEP', plan: 'IEP', status: PLAN_STATUS_ACTIVE },
  { value: 'IEP:initial', label: 'Initial - IEP', plan: 'IEP', status: PLAN_STATUS_INITIAL },
  { value: '504', label: '504', plan: '504', status: PLAN_STATUS_ACTIVE },
  { value: '504:initial', label: 'Initial - 504', plan: '504', status: PLAN_STATUS_INITIAL },
  { value: PLAN_TYPE_NONE, label: "Don't assign a plan", plan: '', status: '' }
]

/**
 * What a SEIS import may map to.
 *
 * SEIS is California's IDEA system: it holds IEPs and service plans, and 504s
 * are administered entirely outside it. Offering 504 here would only ever
 * produce a wrong answer, so it is left out rather than explained away.
 */
export const SEIS_PLAN_TYPE_TARGETS = PLAN_TYPE_TARGETS.filter(
  (target) => !target.value.startsWith('504')
)

/** Turn a stored target value into the plan + status it writes. */
export function resolvePlanTarget(target) {
  const hit = PLAN_TYPE_TARGETS.find((t) => t.value === target)
  return hit ? { plan: hit.plan, status: hit.status } : { plan: '', status: '' }
}

/** Display label for a target value. */
export function planTargetLabel(target) {
  return PLAN_TYPE_TARGETS.find((t) => t.value === target)?.label || target
}

/** The established counterpart of a target: `IEP:initial` → `IEP`. */
export function activePlanTarget(target) {
  const value = String(target || '')
  return value.endsWith(':initial') ? value.slice(0, -':initial'.length) : value
}

/** App already has the student as Initial pending for an IEP. */
export const APP_TRACKS_INITIAL = 'initial'
/** Initial is set but the referral type has not been stored. */
export const APP_TRACKS_INITIAL_UNKNOWN = 'initial-unknown'
/** App has the student as 504 · Initial — a 504 referral, not an IEP one. */
export const APP_TRACKS_INITIAL_504 = 'initial-504'
/** App has an open evaluation beside the student's current plan. */
export const APP_TRACKS_EVAL = 'eval'
/** App has a 504 in force and no open evaluation yet. */
export const APP_TRACKS_504 = '504'

/**
 * How the app already accounts for a SEIS pending initial, from the student's
 * `app` object. '' when it does not.
 *
 * A 504 in force is the documented "504 in force, testing for an IEP" case:
 * the 504 stays, and the pending IEP rides beside it as an evaluation record.
 * It is not a plan question, so it never needs an Initial confirmation.
 *
 * @param {object} app - students/{id}.app
 * @returns {'' | 'initial' | 'initial-unknown' | 'initial-504' | 'eval' | '504'}
 */
export function seisInitialAppTracking(app = {}) {
  const studentData = app?.studentData || {}
  const plan = storedPlanValue(studentData.plan)
  const status = String(studentData.planStatus || '')
    .trim()
    .toLowerCase()
  if (status === PLAN_STATUS_INITIAL) {
    if (plan === '504') return APP_TRACKS_INITIAL_504
    return plan === 'IEP' ? APP_TRACKS_INITIAL : APP_TRACKS_INITIAL_UNKNOWN
  }
  if (String(app?.evalCaseManagerId || '').trim()) return APP_TRACKS_EVAL
  if (plan === '504') return APP_TRACKS_504
  return ''
}

/**
 * Conventional SEIS plan-type codes. Suggestions only.
 *
 * SEIS is an IDEA system, so its plan types are IEP variants; 504 plans are
 * not tracked there and are deliberately absent from this table.
 */
export const PLAN_TYPE_SUGGESTIONS = [
  // Ahead of the IEP pattern on purpose: "Initial IEP" contains "IEP", and the
  // word that matters here is the first one.
  {
    test: /\binitial\b/i,
    target: 'IEP:initial',
    label: 'Marked initial in SEIS — no plan document in place yet'
  },
  {
    test: /^\s*I?300\b/i,
    target: 'IEP:initial',
    label: 'Plan Type 300 — pending initial evaluation'
  },
  // Districts export the code with or without the leading I ("100 …" and
  // "I100 …" are both real), so match the bare number anchored at the start
  // where it is unambiguous rather than anywhere in the string.
  {
    test: /^\s*I?100\b|individualized\s+education\s+program|\bIEP\b/i,
    target: 'IEP',
    label: 'IEP'
  },
  {
    test: /^\s*I?200\b|individual\s+service\s+plan|\bISP\b/i,
    target: 'IEP',
    label: 'ISP — private-school service plan, treated as an IEP here'
  },
  { test: /^\s*504\b|section\s*504/i, target: '504', label: 'Section 504 plan' }
]

/**
 * Suggested target for a raw SEIS plan-type value.
 * @param {string} value - raw cell value, or BLANK_PLAN_TYPE
 * @returns {{ target: string, label: string }|null}
 */
export function suggestPlanTypeTarget(value) {
  if (value === BLANK_PLAN_TYPE) {
    return {
      target: 'IEP:initial',
      label: 'No plan on file yet — most likely an initial IEP awaiting its first meeting'
    }
  }
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return null
  const hit = PLAN_TYPE_SUGGESTIONS.find((entry) => entry.test.test(trimmed))
  return hit ? { target: hit.target, label: hit.label } : null
}

/** Case-insensitive lookup into a saved plan-type map. */
export function lookupPlanType(value, mappings = {}) {
  if (value === BLANK_PLAN_TYPE) return mappings?.[BLANK_PLAN_TYPE] || ''
  const trimmed = String(value ?? '')
    .trim()
    .toLowerCase()
  if (!trimmed) return ''
  for (const [key, target] of Object.entries(mappings || {})) {
    if (String(key).trim().toLowerCase() === trimmed) return target
  }
  return ''
}

/** Display identity for one SEIS row — who a plan-type decision lands on. */
function planRowStudent(row = {}) {
  const info = { ssid: extractSsidFromImportRow(row) || '', name: '', grade: '' }
  let first = ''
  let last = ''
  for (const [key, value] of Object.entries(row)) {
    const text = String(value ?? '').trim()
    if (!text) continue
    const mapped = mapSeisField(String(key).trim())
    if (mapped === 'firstName') first = text
    else if (mapped === 'lastName') last = text
    else if (mapped === 'grade') info.grade = normalizeSeisGrade(text)
  }
  info.name = last && first ? `${last}, ${first}` : last || first
  return info
}

/** Existing referrals that need an explicit type decision, even on dated SEIS rows. */
export function buildSeisInitialTypeReviews(rows = [], appTracking = () => '') {
  const entries = new Map()
  for (const row of rows) {
    const student = planRowStudent(row)
    if (!student.ssid || entries.has(student.ssid)) continue
    const reason = appTracking(student.ssid)
    if (reason !== APP_TRACKS_INITIAL_UNKNOWN && reason !== APP_TRACKS_INITIAL_504) continue
    entries.set(student.ssid, { ...student, appTracking: reason })
  }
  return [...entries.values()]
}

/**
 * Distinct plan-type values in the parsed rows, each with a student count,
 * any saved mapping, and a suggestion. Blank cells collapse into one
 * BLANK_PLAN_TYPE row so "these 6 students have no plan on file" reads as a
 * single decision — but the decision needs faces: each group carries the
 * students it covers (name, grade, SSID) so nobody maps a value blind.
 *
 * @param {object[]} rows - parsed SEIS rows
 * @param {string} column - the CSV header holding the plan type
 * @param {object} [mappings] - saved value -> target map
 * @returns {Array<{value:string, display:string, count:number, mappedTarget:string, suggestion:object|null, isBlank:boolean, students:Array<{ssid:string,name:string,grade:string}>}>}
 */
export function collectPlanTypeValues(rows = [], column = '', mappings = {}) {
  if (!column) return []
  const groups = new Map()

  for (const row of rows) {
    const raw = String(row?.[column] ?? '').trim()
    const value = raw || BLANK_PLAN_TYPE
    const key = value.toLowerCase()
    if (!groups.has(key)) {
      groups.set(key, {
        value,
        display: raw || '(blank — no plan type on file)',
        count: 0,
        isBlank: !raw,
        students: []
      })
    }
    const group = groups.get(key)
    group.count++
    group.students.push(planRowStudent(row))
  }

  for (const group of groups.values()) {
    group.students.sort((a, b) => (a.name || a.ssid).localeCompare(b.name || b.ssid))
  }

  return (
    [...groups.values()]
      .map((group) => ({
        ...group,
        mappedTarget: lookupPlanType(group.value, mappings),
        suggestion: suggestPlanTypeTarget(group.value)
      }))
      // Blanks first: they are the initial-plan decision, the one most likely to
      // be overlooked and the one that changes what the case manager must do.
      .sort((a, b) => Number(b.isBlank) - Number(a.isBlank) || b.count - a.count)
  )
}

/**
 * Apply recognized conventions while preserving explicit mappings.
 * Unrecognized values are intentionally absent until a reviewer maps them.
 */
export function buildSuggestedPlanTypeMappings(groups = [], mappings = {}) {
  const next = { ...(mappings || {}) }
  for (const group of groups) {
    if (!group?.suggestion || lookupPlanType(group.value, next)) continue
    next[group.value] = group.suggestion.target
  }
  return next
}

/**
 * Header names SEIS uses for the plan-type column.
 *
 * The trailing group is not decoration: the real export ships this as
 * "Plan Type (Edu Plan for SpEd Svcs)", and an exact-match rule would leave the
 * column undetected and silently fall back to assuming every student has an IEP.
 */
const PLAN_TYPE_HEADER = /^(plan\s*type|type\s*of\s*plan|plan\s*type\s*code|plan)\s*(\(.*\))?$/i

/**
 * Find the plan-type column in a SEIS export, if it has one.
 * @param {string[]} headers
 * @returns {string} the matching header, or ''
 */
export function detectPlanTypeColumn(headers = []) {
  return headers.find((h) => PLAN_TYPE_HEADER.test(String(h || '').trim())) || ''
}

/**
 * Plan + status for one row, given the confirmed mappings.
 *
 * Falls back to the caller's inference (presence in SEIS means IEP) only when
 * the export has no plan-type column at all. When the column exists but a value
 * is unmapped, nothing is written — an unreviewed code must not silently become
 * a plan.
 *
 * @param {object} args
 * @param {object} args.row
 * @param {string} args.column - '' when the export has no plan-type column
 * @param {object} args.mappings
 * @param {boolean} args.assignIepPlan - the presence-based fallback
 * @returns {{ plan: string, status: string }}
 */
export function resolveRowPlan({
  row = {},
  column = '',
  mappings = {},
  assignIepPlan = true,
  // A per-student decision from the date cross-check (see analyzePlanSignals).
  // It wins outright: somebody looked at this row and said what it is, which
  // beats what the value-level mapping infers.
  targetOverride = ''
} = {}) {
  if (targetOverride) {
    if (targetOverride === PLAN_TYPE_NONE) return { plan: '', status: '' }
    return resolvePlanTarget(targetOverride)
  }
  if (!column) {
    return assignIepPlan ? { plan: 'IEP', status: PLAN_STATUS_ACTIVE } : { plan: '', status: '' }
  }
  const raw = String(row?.[column] ?? '').trim()
  const target = lookupPlanType(raw || BLANK_PLAN_TYPE, mappings)
  if (!target || target === PLAN_TYPE_NONE) return { plan: '', status: '' }
  return resolvePlanTarget(target)
}

// ---------------------------------------------------------------------------
// Date cross-check
// ---------------------------------------------------------------------------

/**
 * Share of rows that must carry a review/reeval date before the absence of one
 * says anything. Below this the column is unpopulated rather than informative,
 * and treating "no date" as evidence would sweep in the whole roster.
 */
export const PLAN_SIGNAL_MIN_DATE_COVERAGE = 0.5

/** Review/reeval headers in this export, used as the second opinion. */
export function detectPlanDateColumns(headers = []) {
  return headers
    .map((header) => String(header ?? '').trim())
    .filter(Boolean)
    .filter((header) => {
      const mapped = mapSeisField(header)
      return mapped === 'reviewDate' || mapped === 'reevalDate' || mapped === 'affirmedIepDate'
    })
}

/** First cell whose header maps to `field`. null when the column is absent. */
function mappedCell(row = {}, field) {
  let seen = false
  let text = ''
  for (const [key, value] of Object.entries(row || {})) {
    if (mapSeisField(String(key).trim()) !== field) continue
    seen = true
    const next = String(value ?? '').trim()
    if (next) text = next
  }
  return seen ? text : null
}

function rowIdentity(row = {}) {
  let ssid = ''
  let first = ''
  let last = ''
  for (const [rawKey, value] of Object.entries(row)) {
    const key = String(rawKey).trim().toLowerCase()
    const text = String(value ?? '').trim()
    if (!text) continue
    if (key === 'ssid') ssid = text
    else if (key === 'first name' || key === 'firstname') first = text
    else if (key === 'last name' || key === 'lastname') last = text
  }
  return {
    ssid,
    name: [first, last].filter(Boolean).join(' '),
    // Extra review-table context. Null-safe: blank when the export lacks them.
    grade: normalizeSeisGrade(mappedCell(row, 'grade') || '') || '',
    caseManager: mappedCell(row, 'caseManager') || ''
  }
}

/**
 * Cross-check the plan type against the compliance dates.
 *
 * An established plan has a next annual review and a triennial reevaluation; a
 * student still in assessment has neither. That makes the dates a second
 * opinion on the plan type — but only a second one. On its own the rule is
 * worthless whenever the date columns are absent or empty, because then every
 * student looks like an initial. Hence the coverage gate, and hence the fact
 * that the interesting output is the disagreements rather than the agreements:
 *
 *   datesWithoutPlanType — blank plan type but real dates on file. Almost
 *     certainly a gap in SEIS data entry, NOT an initial. Flagging these would
 *     pull established students off the caseload, so the default is to leave
 *     their status alone.
 *   planTypeWithoutDates — a plan type is set but no dates exist. Either an
 *     initial whose type was filled in early, or stale data. Worth a look;
 *     nothing is changed automatically.
 *   reviewNotInitial — mapped as initial, but eligibility, Meeting Type, or
 *     Student Exited says otherwise. Shown for a choice. The default drops
 *     the initial flag. An exited row defaults to no plan.
 *   stalePending — Pending, and Plan Type 100 or an affirmed date is already
 *     on the row. Reported so SEIS can be corrected. No evaluation record.
 *
 * @param {object} args
 * @param {object[]} args.rows - parsed SEIS rows
 * @param {string} [args.planTypeColumn]
 * @param {object} [args.planTypeMappings]
 * @param {string[]} [args.dateColumns] - from detectPlanDateColumns
 * @returns {{
 *   hasDateColumns: boolean,
 *   rowsWithDates: number,
 *   totalRows: number,
 *   coverage: number,
 *   usable: boolean,
 *   reason: string,
 *   likelyPendingInitials: object[],
 *   suggestedInitials: object[],
 *   confirmedInitials: object[],
 *   datesWithoutPlanType: object[],
 *   planTypeWithoutDates: object[],
 *   reviewNotInitial: object[],
 *   stalePending: object[],
 *   activeIepButPending: object[]
 * }}
 */
export function analyzePlanSignals({
  rows = [],
  planTypeColumn = '',
  planTypeMappings = {},
  dateColumns = []
} = {}) {
  const totalRows = rows.length
  const hasDateColumns = dateColumns.length > 0

  const rowsWithDates = hasDateColumns
    ? rows.filter((row) => dateColumns.some((column) => String(row?.[column] ?? '').trim())).length
    : 0
  const coverage = totalRows ? rowsWithDates / totalRows : 0

  const result = {
    hasDateColumns,
    rowsWithDates,
    totalRows,
    coverage,
    usable: false,
    reason: '',
    likelyPendingInitials: [],
    suggestedInitials: [],
    confirmedInitials: [],
    datesWithoutPlanType: [],
    planTypeWithoutDates: [],
    reviewNotInitial: [],
    stalePending: [],
    activeIepButPending: []
  }

  // Keep the older names as aliases while parents move to the outcome-oriented
  // API. They deliberately share array identity.
  result.suggestedInitials = result.likelyPendingInitials
  result.activeIepButPending = result.stalePending

  const rowEvidence = rows.map((row) => {
    const identity = rowIdentity(row)
    const raw = planTypeColumn ? String(row?.[planTypeColumn] ?? '').trim() : ''
    const target = planTypeColumn ? lookupPlanType(raw || BLANK_PLAN_TYPE, planTypeMappings) : ''
    const hasDate = dateColumns.some((column) => String(row?.[column] ?? '').trim())
    const meetingRaw = mappedCell(row, 'meetingType')
    const eligibilityRaw = mappedCell(row, 'studentEligibilityStatus')
    const exitedRaw = mappedCell(row, 'studentExited')
    const evidence = {
      row,
      identity,
      raw,
      target,
      hasDate,
      impliesInitial: typeof target === 'string' && target.endsWith(':initial'),
      meetingRaw,
      eligibilityRaw,
      exitedRaw,
      // An explicit Pending eligibility with a blank plan and no dates IS the
      // pending-initial pattern (seis-import-flow rule 3). For such a student
      // the Meeting Type on the current IEP forms is the initial being
      // prepared (code 10), not contrary evidence — so the meeting-type block
      // only applies when eligibility does not say Pending. Code 10 beside
      // Plan Type 100 or real dates never reaches this gate; those rows are
      // already established by the date cross-check.
      blockedByMeeting:
        !normalizeSeisEligibilityStatus(eligibilityRaw || '').pending &&
        meetingTypeBlocksInitial(meetingRaw),
      blockedByEligibility: eligibilityBlocksInitial(eligibilityRaw),
      blockedByExit: exitedBlocksInitial(exitedRaw)
    }

    const affirmed = mappedCell(row, 'affirmedIepDate')
    if (
      isStaleSeisPending({
        eligibilityStatus: eligibilityRaw || '',
        planType: raw,
        affirmedIepDate: affirmed || ''
      })
    ) {
      result.stalePending.push({
        ...identity,
        planTypeValue: raw,
        affirmedIepDate: affirmed || ''
      })
    }

    if (
      evidence.impliesInitial &&
      !hasDate &&
      !evidence.blockedByMeeting &&
      !evidence.blockedByEligibility &&
      !evidence.blockedByExit
    ) {
      result.likelyPendingInitials.push({
        ...identity,
        planTypeValue: raw,
        mappedTarget: target,
        eligibilityStatus: eligibilityRaw || '',
        meetingType: meetingRaw || ''
      })
    }

    return evidence
  })

  if (!hasDateColumns) {
    result.reason =
      'This export has no plan review or reevaluation dates, so there is nothing to check the plan ' +
      'type against.'
    return result
  }
  if (coverage < PLAN_SIGNAL_MIN_DATE_COVERAGE) {
    result.reason =
      `Only ${rowsWithDates} of ${totalRows} rows carry a review or reevaluation date. That is too ` +
      'sparse to read a missing date as evidence — it would flag most of the roster as initial.'
    return result
  }

  result.usable = true

  for (const evidence of rowEvidence) {
    const {
      identity,
      raw,
      target,
      hasDate,
      impliesInitial,
      meetingRaw,
      eligibilityRaw,
      blockedByMeeting,
      blockedByEligibility,
      blockedByExit
    } = evidence

    if (
      impliesInitial &&
      !hasDate &&
      !blockedByMeeting &&
      !blockedByEligibility &&
      !blockedByExit
    ) {
      result.confirmedInitials.push({
        ...identity,
        planTypeValue: raw,
        mappedTarget: target
      })
    } else if (
      impliesInitial &&
      !hasDate &&
      (blockedByMeeting || blockedByEligibility || blockedByExit)
    ) {
      const why = blockedByExit
        ? 'Student Exited is Yes'
        : blockedByEligibility
          ? `Eligibility: ${eligibilityRaw}`
          : `Meeting Type: ${meetingRaw}`
      result.reviewNotInitial.push({
        ...identity,
        planTypeValue: raw,
        mappedTarget: target,
        defaultTarget: blockedByExit ? PLAN_TYPE_NONE : activePlanTarget(target),
        reason: why
      })
    } else if (impliesInitial && hasDate) {
      // Default is the mapping with the initial flag stripped rather than a
      // hardcoded IEP, so a district that maps blanks to something else still
      // gets the established form of their own choice.
      result.datesWithoutPlanType.push({
        ...identity,
        planTypeValue: raw,
        mappedTarget: target,
        defaultTarget: activePlanTarget(target),
        dates: dateColumns
          .map((column) => ({ column, value: String(evidence.row?.[column] ?? '').trim() }))
          .filter((entry) => entry.value)
      })
    } else if (target && target !== PLAN_TYPE_NONE && !hasDate) {
      result.planTypeWithoutDates.push({
        ...identity,
        planTypeValue: raw,
        mappedTarget: target,
        defaultTarget: target
      })
    }
  }

  return result
}

/**
 * The three reviewer-facing outcomes, independent of how a component lays
 * them out. Legacy signal names remain accepted for callers migrating in
 * stages.
 */
export function buildPlanSignalOutcomes(signals = {}) {
  const likelyPendingInitials =
    signals.likelyPendingInitials || signals.suggestedInitials || signals.confirmedInitials || []
  const activeIepButPending = signals.activeIepButPending || signals.stalePending || []
  const activePendingSsids = new Set(
    activeIepButPending.map((entry) => String(entry?.ssid || '').trim()).filter(Boolean)
  )

  const needsDecision = [
    ...(signals.datesWithoutPlanType || []).map((entry) => ({
      ...entry,
      decisionType: 'active-dates-without-plan-type',
      decisionReason:
        'Review or reevaluation dates show an active IEP even though SEIS Plan Type is blank.'
    })),
    ...(signals.planTypeWithoutDates || []).map((entry) => ({
      ...entry,
      decisionType: 'plan-type-without-dates',
      decisionReason:
        'SEIS provides a plan type, but no annual review or reevaluation date is present.'
    })),
    ...(signals.reviewNotInitial || []).map((entry) => ({
      ...entry,
      decisionType: 'initial-evidence-conflict',
      decisionReason: entry.reason || 'Other SEIS evidence conflicts with a pending initial.'
    }))
  ].filter((entry) => !activePendingSsids.has(String(entry?.ssid || '').trim()))

  return { likelyPendingInitials, activeIepButPending, needsDecision }
}

/** Whether one suggested initial has been explicitly confirmed. */
export function isInitialPlanConfirmed(confirmations = {}, ssid = '') {
  const key = String(ssid || '').trim()
  if (!key) return false
  if (confirmations instanceof Set) return confirmations.has(key) || confirmations.has(ssid)
  if (Array.isArray(confirmations)) {
    return confirmations.some((value) => String(value || '').trim() === key)
  }
  return confirmations?.[key] === true
}

/** Suggested initials still awaiting a per-student decision. */
export function getUnconfirmedInitialPlanStudents(signals = {}, confirmations = {}) {
  return buildPlanSignalOutcomes(signals).likelyPendingInitials.filter(
    (entry) => !isInitialPlanConfirmed(confirmations, entry.ssid)
  )
}

/**
 * Overrides that make per-student confirmation enforceable by an import
 * parent. Unconfirmed suggestions resolve to "do not assign"; confirmed
 * students resolve to the exact initial target that was reviewed.
 *
 * This helper is intentionally not merged into buildPlanTargetOverrides:
 * parents can adopt the confirmation gate without changing existing imports
 * until their UI is wired to collect the decisions.
 */
export function buildInitialConfirmationOverrides(signals = {}, confirmations = {}) {
  const overrides = {}
  for (const entry of buildPlanSignalOutcomes(signals).likelyPendingInitials) {
    if (!entry.ssid) continue
    overrides[entry.ssid] = isInitialPlanConfirmed(confirmations, entry.ssid)
      ? entry.mappedTarget || 'IEP:initial'
      : PLAN_TYPE_NONE
  }
  return overrides
}

/**
 * Default per-student decisions from the cross-check, keyed by SSID.
 *
 * Only the "has dates but no plan type" case needs one, because it is the only
 * place the default departs from what the value mapping says: the dates prove
 * the plan is established, so the initial flag comes off. The other conflict
 * group already imports as its mapped value, so it needs no entry here.
 *
 * Nothing in here promotes a student to initial on missing dates alone — that
 * call stays with the reviewer.
 *
 * @param {ReturnType<typeof analyzePlanSignals>} signals
 * @returns {Record<string, string>} ssid -> plan target
 */
export function buildPlanTargetOverrides(signals) {
  const overrides = {}
  if (!signals?.usable) return overrides
  for (const entry of [
    ...(signals.datesWithoutPlanType || []),
    ...(signals.reviewNotInitial || [])
  ]) {
    if (entry.ssid && entry.defaultTarget && entry.defaultTarget !== entry.mappedTarget) {
      overrides[entry.ssid] = entry.defaultTarget
    }
  }
  return overrides
}
